/**
 * @file drawing-tools-handler.ts
 * @description Manages raster-based drawing tools including pen, brush, eraser, spray, and flood fill.
 */

import { g, Tool } from '@hcie/core';
import {
    drawPen,
    drawBrush,
    drawEraser,
    drawSpray,
    floodFill,
    drawRedEye,
    drawSpotHealing,
    drawCloneStamp
} from '@hcie/tools';
import type { BrushJob, BrushResult } from '@hcie/tools/workers/brush-worker';
import { getActiveLayer } from '../layer-service';
import { renderLayers } from '../render-engine';
import { updateLayerPanel } from '../layers';
import { setToolWarning } from '../ui/render-helpers';
import { tempCtx } from '../rendering-loop';
import { parseCssColorToRgba, EventBus } from '@hcie/shared';

// ─── Heavy Brush Worker (oil, charcoal) ──────────────────────────────────────
// Offloads CPU-heavy particle rendering to a Web Worker so the main thread
// stays responsive. Serial dispatch ensures each job sees the latest canvas state.

const HEAVY_BRUSH_STYLES = new Set(['oil', 'charcoal']);

let _brushWorker: Worker | null = null;
let _workerBusy   = false;
/** Deferred job: captured when worker is busy. Applied once current job completes. */
let _pendingPos: { x: number; y: number } | null = null;

function getHeavyBrushWorker(): Worker {
    if (!_brushWorker) {
        _brushWorker = new Worker(
            new URL('../../node_modules/@hcie/tools/src/workers/brush-worker.ts', import.meta.url),
            { type: 'module' }
        );
        _brushWorker.onmessage = (e: MessageEvent<BrushResult>) => {
            const { imageData, originX, originY } = e.data;
            const ctx = tempCtx as CanvasRenderingContext2D;
            ctx.putImageData(imageData, originX, originY);
            EventBus.emit('RENDER_LAYERS');

            if (_pendingPos) {
                const { x, y } = _pendingPos;
                _pendingPos = null;
                _postHeavyBrushJob(ctx, x, y); // serial: post next from latest canvas state
            } else {
                _workerBusy = false;
            }
        };
        _brushWorker.onerror = (err) => {
            console.error('[BrushWorker] error:', err);
            _workerBusy = false;
            _pendingPos = null;
        };
    }
    return _brushWorker;
}

function _postHeavyBrushJob(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    const radius  = (g.pen_width || 20) / 2;
    const padding = Math.ceil(radius) + 2;
    const canvasW = ctx.canvas.width;
    const canvasH = ctx.canvas.height;

    const ox = Math.max(0, Math.floor(x - radius - padding));
    const oy = Math.max(0, Math.floor(y - radius - padding));
    const rw = Math.min(canvasW - ox, Math.ceil(radius * 2 + padding * 2));
    const rh = Math.min(canvasH - oy, Math.ceil(radius * 2 + padding * 2));

    if (rw <= 0 || rh <= 0) { _workerBusy = false; return; }

    const snap  = ctx.getImageData(ox, oy, rw, rh);
    const style = (g.brush_style || 'oil') as BrushJob['style'];

    const job: BrushJob = {
        style,
        imageData: snap,
        x, y,
        radius,
        color:   g.pen_color   || '#000000',
        opacity: g.pen_opacity  ?? 1.0,
        flow:    (g as any).brush_flow ?? 1.0,
        density: Math.max(5, Math.floor((g.pen_width || 20) * 1.5)),
        originX: ox,
        originY: oy,
    };

    _workerBusy = true;
    getHeavyBrushWorker().postMessage(job, [snap.data.buffer]);
}

function dispatchHeavyBrush(x: number, y: number): void {
    const ctx = tempCtx as CanvasRenderingContext2D;
    if (_workerBusy) {
        _pendingPos = { x, y }; // coalesce: keep only latest pending position
    } else {
        _postHeavyBrushJob(ctx, x, y);
    }
}

// Track previous eraser position for smooth freehand strokes
let eraserPrevX = 0;
let eraserPrevY = 0;

// Minimum distance (canvas-space pixels) between consecutive brush dabs.
// Eliminates redundant dabs when the pointer barely moves, reducing CPU load
// on large canvases without any visible quality loss.
const MIN_BRUSH_DIST_SQ = 1.0; // 1px² = 1px Euclidean minimum
const MIN_PEN_DIST_SQ   = 0.25; // 0.5px — pen needs higher fidelity

let _brushLastX = 0;
let _brushLastY = 0;
let _penLastX   = 0;
let _penLastY   = 0;

export class DrawingToolsHandler {
    /**
     * @function handleMouseDown
     * @description Initiates a drawing operation based on the active tool.
     * @param {MouseEvent} event - The native mouse event.
     * @returns {boolean} - Returns true if the tool handled the event.
     */
    public static handleMouseDown(event: MouseEvent): boolean {
        // [CRITICAL_LOGIC_START]
        const currentToolId: string = g.current_tool.id;
        const activeLayer = getActiveLayer();
        
        if (!activeLayer || !activeLayer.visible || !activeLayer.ctx) {
            return false;
        }

        // [New Rule] Block raster drawing on vector layers
        if (activeLayer.type === 'vector') {
            setToolWarning("Vektör katmanı üzerine raster çizim yapılamaz!");
            return false;
        }

        const layerContext = activeLayer.ctx as CanvasRenderingContext2D;
        const temporaryContext = tempCtx as CanvasRenderingContext2D;

        switch (currentToolId) {
            case Tool.Pen.id:
                g.drawing = true;
                _penLastX = g.pX;
                _penLastY = g.pY;
                drawPen(event, temporaryContext);
                return true;

            case Tool.Brush.id:
                g.drawing = true;
                _brushLastX = g.pX;
                _brushLastY = g.pY;
                if (HEAVY_BRUSH_STYLES.has(g.brush_style || '')) {
                    dispatchHeavyBrush(g.pX, g.pY);
                } else {
                    drawBrush(temporaryContext, g.pX, g.pY);
                }
                return true;

            case Tool.Eraser.id:
                g.drawing = true;
                eraserPrevX = g.pX;
                eraserPrevY = g.pY;
                // Stamp initial dot at click position
                drawEraser(temporaryContext, g.pX, g.pY, g.pX, g.pY);
                return true;

            case Tool.Spray.id:
                g.drawing = true;
                drawSpray(temporaryContext, event);
                return true;

            case Tool.Flood_Fill.id:
                this.performFloodFill(layerContext);
                return true;

            case 'btn-red-eye':
                g.drawing = true;
                const radiusMouseDown: number = g.pen_width / 2;
                drawRedEye(layerContext, temporaryContext, g.pX, g.pY, radiusMouseDown, g.brush_hardness);
                return true;

            case Tool.SpotHealing.id:
                g.drawing = true;
                drawSpotHealing(temporaryContext, g.pX, g.pY);
                return true;

            case Tool.CloneStamp.id:
                g.drawing = true;
                drawCloneStamp(temporaryContext, g.pX, g.pY);
                return true;

            default:
                return false;
        }
        // [CRITICAL_LOGIC_END]
    }

    /**
     * @function handleMouseMove
     * @description Continues the drawing operation while the mouse is moving.
     * @param {MouseEvent} event - The native mouse event.
     * @returns {boolean} - Returns true if handled.
     */
    public static handleMouseMove(event: MouseEvent): boolean {
        if (!g.drawing) return false;

        const currentToolId: string = g.current_tool.id;
        const activeLayer = getActiveLayer();
        if (!activeLayer || !activeLayer.ctx) return false;

        const temporaryContext = tempCtx as CanvasRenderingContext2D;

        // [CRITICAL_LOGIC_START]
        switch (currentToolId) {
            case Tool.Pen.id: {
                const dxPen = g.pX - _penLastX;
                const dyPen = g.pY - _penLastY;
                if (dxPen * dxPen + dyPen * dyPen < MIN_PEN_DIST_SQ) return true;
                _penLastX = g.pX;
                _penLastY = g.pY;
                drawPen(event, temporaryContext);
                return true;
            }
            case Tool.Brush.id: {
                const dxBrush = g.pX - _brushLastX;
                const dyBrush = g.pY - _brushLastY;
                if (dxBrush * dxBrush + dyBrush * dyBrush < MIN_BRUSH_DIST_SQ) return true;
                _brushLastX = g.pX;
                _brushLastY = g.pY;
                if (HEAVY_BRUSH_STYLES.has(g.brush_style || '')) {
                    dispatchHeavyBrush(g.pX, g.pY);
                } else {
                    drawBrush(temporaryContext, g.pX, g.pY);
                }
                return true;
            }

            case Tool.Eraser.id:
                // Interpolate from previous position to current for smooth freehand path
                drawEraser(temporaryContext, eraserPrevX, eraserPrevY, g.pX, g.pY);
                eraserPrevX = g.pX;
                eraserPrevY = g.pY;
                return true;

            case Tool.Spray.id:
                drawSpray(temporaryContext, event);
                return true;

            case 'btn-red-eye':
                const radiusMouseMove: number = g.pen_width / 2;
                drawRedEye(activeLayer.ctx as CanvasRenderingContext2D, temporaryContext, g.pX, g.pY, radiusMouseMove, g.brush_hardness);
                return true;

            case Tool.SpotHealing.id:
                drawSpotHealing(temporaryContext, g.pX, g.pY);
                return true;

            case Tool.CloneStamp.id:
                drawCloneStamp(temporaryContext, g.pX, g.pY);
                return true;

            default:
                return false;
        }
        // [CRITICAL_LOGIC_END]
    }

    /**
     * @function performFloodFill
     * @description Executes the flood fill algorithm on the active layer.
     * @private
     */
    private static performFloodFill(ctx: CanvasRenderingContext2D): void {
        const fillColorRgba = parseCssColorToRgba(g.pen_color);
        const tolerance = { 
            red: g.fill_tolerance, 
            green: g.fill_tolerance, 
            blue: g.fill_tolerance, 
            alpha: g.fill_tolerance 
        };
        
        floodFill(g.pX, g.pY, ctx, fillColorRgba, tolerance);
        
        renderLayers();
        updateLayerPanel();
    }
}
